// ApexManufacturingLayout.js - Premium Industrial Manufacturing Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-300 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200 dark:border-slate-600 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function ApexManufacturingLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#1E293B"; // Slate
  const secondary = T.secondaryColor || "#F59E0B"; // Amber accent
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeIndustry, setActiveIndustry] = useState("all");
  const [showStats, setShowStats] = useState(false);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Manufacturing hero images - DIRECTLY FROM CONFIG
  const manufacturingHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1400&q=80",
    },
    {
      field: "apexHeroImage2",
      src: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1400&q=80",
    },
    {
      field: "apexHeroImage3",
      src: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=1400&q=80",
    },
    {
      field: "apexHeroImage4",
      src: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1400&q=80",
    },
  ];

  // Manufacturing Products - Industrial Equipment
  const manufacturingProducts = [
    // Industrial Machinery
    {
      id: 1,
      nameKey: "product1_name",
      name: "CNC Milling Machine (5-Axis)",
      priceKey: "product1_price",
      price: 1499999,
      originalPriceKey: "product1_original_price",
      originalPrice: 1999999,
      imageField: "product1_image",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&q=80",
      categoryKey: "product1_category",
      category: "Machinery",
      subCategory: "CNC",
      descriptionKey: "product1_description",
      description: "High-precision 5-axis CNC milling machine with automated tool changer. Ideal for complex manufacturing.",
      badge: "Premium",
      badgeColor: "#1E293B",
      quantity: "1 unit",
      brand: "ApexTech",
      type: "Industrial",
      power: "15kW",
      warranty: "5 Years"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Industrial Robotic Arm",
      priceKey: "product2_price",
      price: 899999,
      originalPriceKey: "product2_original_price",
      originalPrice: 1199999,
      imageField: "product2_image",
      image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=400&q=80",
      categoryKey: "product2_category",
      category: "Machinery",
      subCategory: "Robotics",
      descriptionKey: "product2_description",
      description: "6-axis industrial robotic arm for automated production lines. High precision and speed.",
      badge: "Smart",
      badgeColor: "#F59E0B",
      quantity: "1 unit",
      brand: "ApexRobotics",
      type: "Industrial",
      power: "7.5kW",
      warranty: "3 Years"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Hydraulic Press (200 Ton)",
      priceKey: "product3_price",
      price: 2499999,
      originalPriceKey: "product3_original_price",
      originalPrice: 2999999,
      imageField: "product3_image",
      image: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=400&q=80",
      categoryKey: "product3_category",
      category: "Machinery",
      subCategory: "Hydraulics",
      descriptionKey: "product3_description",
      description: "Heavy-duty hydraulic press with 200-ton capacity. Robust design for demanding applications.",
      badge: "Heavy Duty",
      badgeColor: "#1E293B",
      quantity: "1 unit",
      brand: "ApexPress",
      type: "Industrial",
      power: "30kW",
      warranty: "5 Years"
    },
    
    // Industrial Tools
    {
      id: 4,
      nameKey: "product4_name",
      name: "Industrial Welding System",
      priceKey: "product4_price",
      price: 499999,
      originalPriceKey: "product4_original_price",
      originalPrice: 699999,
      imageField: "product4_image",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&q=80",
      categoryKey: "product4_category",
      category: "Tools",
      subCategory: "Welding",
      descriptionKey: "product4_description",
      description: "Advanced MIG/TIG welding system with digital controls. Perfect for precision welding.",
      badge: "Professional",
      badgeColor: "#F59E0B",
      quantity: "1 unit",
      brand: "ApexWeld",
      type: "Industrial",
      power: "8kW",
      warranty: "2 Years"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Industrial Laser Cutter",
      priceKey: "product5_price",
      price: 1899999,
      originalPriceKey: "product5_original_price",
      originalPrice: 2499999,
      imageField: "product5_image",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&q=80",
      categoryKey: "product5_category",
      category: "Tools",
      subCategory: "Laser",
      descriptionKey: "product5_description",
      description: "High-power fiber laser cutter for precision metal cutting. Clean and efficient.",
      badge: "Precision",
      badgeColor: "#1E293B",
      quantity: "1 unit",
      brand: "ApexLaser",
      type: "Industrial",
      power: "6kW",
      warranty: "3 Years"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Industrial Drill Press",
      priceKey: "product6_price",
      price: 299999,
      originalPriceKey: "product6_original_price",
      originalPrice: 399999,
      imageField: "product6_image",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&q=80",
      categoryKey: "product6_category",
      category: "Tools",
      subCategory: "Drilling",
      descriptionKey: "product6_description",
      description: "Heavy-duty industrial drill press with variable speed and digital depth control.",
      badge: "Durable",
      badgeColor: "#F59E0B",
      quantity: "1 unit",
      brand: "ApexDrill",
      type: "Industrial",
      power: "5kW",
      warranty: "2 Years"
    },
    
    // Industrial Parts
    {
      id: 7,
      nameKey: "product7_name",
      name: "Industrial Bearings Set",
      priceKey: "product7_price",
      price: 49999,
      originalPriceKey: "product7_original_price",
      originalPrice: 69999,
      imageField: "product7_image",
      image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=400&q=80",
      categoryKey: "product7_category",
      category: "Parts",
      subCategory: "Bearings",
      descriptionKey: "product7_description",
      description: "Precision industrial bearings set for heavy machinery. High load capacity.",
      badge: "Essential",
      badgeColor: "#1E293B",
      quantity: "Set of 10",
      brand: "ApexParts",
      type: "Industrial",
      material: "Steel",
      warranty: "1 Year"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Hydraulic Cylinder Kit",
      priceKey: "product8_price",
      price: 149999,
      originalPriceKey: "product8_original_price",
      originalPrice: 199999,
      imageField: "product8_image",
      image: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=400&q=80",
      categoryKey: "product8_category",
      category: "Parts",
      subCategory: "Hydraulics",
      descriptionKey: "product8_description",
      description: "Complete hydraulic cylinder kit with seals and mounting brackets.",
      badge: "Professional",
      badgeColor: "#F59E0B",
      quantity: "Kit",
      brand: "ApexHydraulics",
      type: "Industrial",
      material: "Chrome Plated",
      warranty: "2 Years"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "Industrial Gears Set",
      priceKey: "product9_price",
      price: 79999,
      originalPriceKey: "product9_original_price",
      originalPrice: 99999,
      imageField: "product9_image",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&q=80",
      categoryKey: "product9_category",
      category: "Parts",
      subCategory: "Gears",
      descriptionKey: "product9_description",
      description: "Precision ground industrial gears for heavy machinery applications.",
      badge: "Precision",
      badgeColor: "#1E293B",
      quantity: "Set of 5",
      brand: "ApexGears",
      type: "Industrial",
      material: "Alloy Steel",
      warranty: "1 Year"
    }
  ];

  // Manufacturing Categories
  const manufacturingCategories = [
    { key: "all", icon: "🏭", label: "All Products", color: "#1E293B" },
    { key: "machinery", icon: "⚙️", label: "Machinery", color: "#F59E0B" },
    { key: "tools", icon: "🔧", label: "Industrial Tools", color: "#1E293B" },
    { key: "parts", icon: "🔩", label: "Industrial Parts", color: "#F59E0B" },
  ];

  // Industries Served
  const industriesServed = [
    { key: "all", label: "All Industries", icon: "🏭" },
    { key: "automotive", label: "Automotive", icon: "🚗" },
    { key: "aerospace", label: "Aerospace", icon: "✈️" },
    { key: "construction", label: "Construction", icon: "🏗️" },
    { key: "electronics", label: "Electronics", icon: "🔌" },
    { key: "pharmaceutical", label: "Pharmaceutical", icon: "💊" },
  ];

  // Manufacturing Capabilities
  const manufacturingCapabilities = [
    { 
      id: 1, 
      icon: "🔬", 
      title: "Precision Engineering",
      desc: "High-tolerance manufacturing with 0.01mm precision"
    },
    { 
      id: 2, 
      icon: "🤖", 
      title: "Automated Production",
      desc: "AI-driven manufacturing with robotic automation"
    },
    { 
      id: 3, 
      icon: "📐", 
      title: "Custom Fabrication",
      desc: "Tailored solutions for unique industrial needs"
    },
    { 
      id: 4, 
      icon: "💡", 
      title: "Innovation R&D",
      desc: "Continuous improvement and innovation focus"
    },
  ];

  // Manufacturing Projects
  const manufacturingProjects = [
    {
      id: 1,
      titleKey: "project1_title",
      title: "Automotive Assembly Line",
      descKey: "project1_desc",
      desc: "Complete automated assembly line for automotive manufacturer",
      imageField: "project1_image",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80",
      client: "AutoGlobal Industries",
      duration: "6 months",
      value: "$5.2M"
    },
    {
      id: 2,
      titleKey: "project2_title",
      title: "Aerospace Components",
      descKey: "project2_desc",
      desc: "Precision manufacturing of aerospace-grade components",
      imageField: "project2_image",
      image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&q=80",
      client: "AeroSpace Solutions",
      duration: "12 months",
      value: "$12.8M"
    },
    {
      id: 3,
      titleKey: "project3_title",
      title: "Green Energy Equipment",
      descKey: "project3_desc",
      desc: "Sustainable manufacturing for renewable energy industry",
      imageField: "project3_image",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&q=80",
      client: "EcoEnergy Corp",
      duration: "8 months",
      value: "$8.4M"
    }
  ];

  // Manufacturing Journal
  const manufacturingPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "Industry 4.0 Manufacturing",
      descKey: "post1_desc",
      desc: "How smart manufacturing is transforming production",
      fullContentKey: "post1_content",
      fullContent: "Industry 4.0 represents the fourth industrial revolution, characterized by smart factories, IoT integration, and data-driven decision making. This transformation is revolutionizing manufacturing processes, improving efficiency, and creating new opportunities.",
      imageField: "post1_image",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80",
      dateKey: "post1_date",
      date: "January 20, 2025",
      authorKey: "post1_author",
      author: "James Anderson",
      category: "Industry 4.0"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Sustainable Manufacturing",
      descKey: "post2_desc",
      desc: "Green practices for a sustainable future",
      fullContentKey: "post2_content",
      fullContent: "Sustainable manufacturing practices are becoming increasingly important as industries strive to reduce their environmental footprint. From energy efficiency to waste reduction, discover how manufacturers are embracing green practices.",
      imageField: "post2_image",
      image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80",
      dateKey: "post2_date",
      date: "January 15, 2025",
      authorKey: "post2_author",
      author: "Maria Santos",
      category: "Sustainability"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Future of Automation",
      descKey: "post3_desc",
      desc: "Robotics and AI shaping the factory of tomorrow",
      fullContentKey: "post3_content",
      fullContent: "The future of manufacturing is increasingly automated, with AI and robotics playing central roles. From autonomous factories to predictive maintenance, explore how automation is reshaping the manufacturing landscape.",
      imageField: "post3_image",
      image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=800&q=80",
      dateKey: "post3_date",
      date: "January 10, 2025",
      authorKey: "post3_author",
      author: "David Kim",
      category: "Automation"
    }
  ];

  // Manufacturing Stats
  const manufacturingStats = [
    { number: "500+", label: "Completed Projects" },
    { number: "98%", label: "Quality Rate" },
    { number: "150+", label: "Expert Team" },
    { number: "50+", label: "Global Clients" },
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_products", label: "PRODUCTS", page: "products" },
    { key: "nav_projects", label: "PROJECTS", page: "projects" },
    { key: "nav_capabilities", label: "CAPABILITIES", page: "capabilities" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🏭 MANUFACTURING EXCELLENCE SINCE 1995",
    "✦ B2B & INDUSTRIAL SOLUTIONS",
    "⚙️ PRECISION ENGINEERING",
    "✦ GLOBAL REACH, LOCAL EXPERTISE"
  ];

  // Floating particles - industrial style
  const particles = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 3,
    delay: Math.random() * 5,
    duration: 6 + Math.random() * 8,
    opacity: 0.03 + Math.random() * 0.06,
  }));

  // Geometric shapes for industrial feel
  const geometricShapes = [
    { type: "square", size: 40, top: 15, left: 5, delay: 0 },
    { type: "circle", size: 30, top: 75, left: 85, delay: 2 },
    { type: "triangle", size: 45, top: 45, left: 92, delay: 4 },
    { type: "diamond", size: 35, top: 85, left: 10, delay: 1 },
    { type: "hexagon", size: 50, top: 30, left: 88, delay: 3 },
  ];

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % manufacturingHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const statsSection = document.getElementById('stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          setShowStats(true);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openProjectModal = useCallback((project, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedProject(project);
  }, [p.previewEditMode, p.activeEditField]);

  const closeProjectModal = useCallback(() => {
    setSelectedProject(null);
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedProject(null);
      setSelectedPost(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || manufacturingHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = manufacturingHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, manufacturingHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "APEX MANUFACTURING",
    title: p.storeTheme?.heroTitle || "Precision Engineering, Global Impact",
    subtitle: p.storeTheme?.heroSubtitle || "Delivering industrial excellence for 30 years",
    primaryCta: p.storeTheme?.primaryCtaText || "Explore Products",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Projects",
  };

  // Get editable products with imageField
  const editableManufacturingProducts = manufacturingProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable projects with imageField
  const editableManufacturingProjects = manufacturingProjects.map((project) => ({
    ...project,
    image: p.storeTheme?.[project.imageField] || project.image,
  }));

  // Get editable posts with imageField
  const editableManufacturingPosts = manufacturingPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items
  const getFilteredItems = (category) => {
    if (category === "all") return editableManufacturingProducts;
    const map = {
      "machinery": "Machinery",
      "tools": "Tools",
      "parts": "Parts",
    };
    return editableManufacturingProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get filtered projects
  const getFilteredProjects = (industry) => {
    if (industry === "all") return editableManufacturingProjects;
    return editableManufacturingProjects;
  };

  const filteredProjects = getFilteredProjects(activeIndustry);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -50, y: -20, rotate: -3 },
      { x: 50, y: -20, rotate: 3 },
      { x: -50, y: 20, rotate: -2 },
      { x: 50, y: 20, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-slate-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Industrial Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute industrial-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size * 3}px`,
              height: `${particle.size * 3}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(15 + Math.random() * 20).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>

      {/* Geometric Shapes - Industrial Aesthetic */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {geometricShapes.map((shape, idx) => (
          <div
            key={idx}
            className={`absolute geometric-${shape.type}`}
            style={{
              top: `${shape.top}%`,
              left: `${shape.left}%`,
              width: `${shape.size}px`,
              height: `${shape.size}px`,
              borderColor: `${primary}${Math.floor(8 + Math.random() * 10).toString(16)}`,
              animationDelay: `${shape.delay}s`,
              animationDuration: `${8 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar - Industrial Style */}
      <div className="relative bg-slate-900 text-slate-300 py-2.5 overflow-hidden border-b-2 border-slate-700/30">
        <div className="flex items-center justify-center gap-6 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-5 text-xs tracking-wider font-medium">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Industrial Minimal */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-white/95 backdrop-blur-xl shadow-lg py-2 border-b border-slate-200" : "bg-white/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center text-lg border-2 border-amber-400/50 shadow-lg shadow-slate-900/20">
              <span className="text-amber-400 font-bold text-sm">⚙</span>
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Apex Manufacturing"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-amber-600" 
                    : "text-slate-600/70 hover:text-slate-900"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-amber-500 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-slate-100">
                <FaHeart className="text-lg text-slate-600/70 hover:text-slate-900 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={false} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-slate-100">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => {
                  navigateTo(item.page, e);
                  setMobileMenuOpen(false);
                }}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-slate-100 text-amber-600" 
                    : "text-slate-600/70 hover:text-slate-900"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {manufacturingHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Manufacturing ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-50/80 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Hero edit buttons */}
              {p.previewEditMode && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-300/40 bg-slate-900/35 px-3 py-2 backdrop-blur-sm">
                  {manufacturingHeroImageConfigs.map((image, index) => (
                    <button
                      key={image.field}
                      type="button"
                      onClick={(event) => handleEditHeroImage(event, image.field)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${index === activeSlide ? "bg-amber-500 text-white" : "bg-white/10 text-slate-100 hover:bg-white/20"}`}
                    >
                      Hero {index + 1}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="absolute bottom-0 left-0 right-0 z-20">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                  <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#F8FAFC" opacity="0.95" />
                </svg>
              </div>
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-slate-800/40 backdrop-blur-md rounded-lg border border-amber-400/30">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                    <span className="text-slate-200 text-xs tracking-[0.2em] uppercase font-semibold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-slate-200/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("products", e)} 
                    className="group px-8 py-4 rounded-lg bg-amber-500 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="products"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("projects", e)} 
                    className="px-8 py-4 rounded-lg border-2 border-amber-400/50 text-slate-200 font-semibold tracking-wide transition-all duration-300 hover:bg-amber-600/30 hover:border-amber-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="projects"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-slate-300/60 text-[10px] tracking-wider font-medium">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-amber-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-amber-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-6 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-lg shadow-lg border border-slate-200">
              <span className="text-amber-600 text-sm">⚙</span>
              <span className="text-slate-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="ISO 9001:2024 CERTIFIED"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-amber-600 text-sm">⚙</span>
            </div>
          </div>
          
          {/* Stats Section - Animated */}
          <section id="stats-section" className="w-full px-4 md:px-8 py-12 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {manufacturingStats.map((stat, idx) => (
                <div 
                  key={idx} 
                  className={`text-center transition-all duration-700 ${
                    showStats ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                  }`}
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <p className="text-4xl md:text-5xl font-bold text-amber-500">
                    {stat.number}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>
          
          {/* Manufacturing Capabilities */}
          <section className="w-full px-4 md:px-8 py-12 bg-slate-50">
            <div className="text-center mb-10">
              <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="capabilities_badge" 
                  value="Our Expertise"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="capabilities"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="capabilities_title" 
                  value="Manufacturing Capabilities"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="capabilities"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {manufacturingCapabilities.map((cap, idx) => (
                <div 
                  key={cap.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <div className="relative overflow-hidden rounded-lg p-6 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-slate-200 hover:border-amber-300">
                    <div className="text-5xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {cap.icon}
                    </div>
                    <h3 className="font-semibold text-slate-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cap_${cap.id}_title`} 
                        value={cap.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="capabilities"
                      />
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cap_${cap.id}_desc`} 
                        value={cap.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="capabilities"
                      />
                    </p>
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_badge" 
                  value="Product Categories"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="categories_title" 
                  value="Browse Our Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {manufacturingCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("products", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("products", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-lg p-5 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-slate-50 border border-slate-200 hover:border-amber-300">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-slate-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="products"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8 bg-slate-50">
            <div className="text-center mb-10">
              <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Featured Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Industrial Solutions"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableManufacturingProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-lg bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-slate-200 hover:border-amber-300">
                      <div className="relative h-48 overflow-hidden bg-slate-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-slate-800/80 text-slate-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-slate-500">{product.brand}</span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-slate-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-amber-600">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-slate-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-lg">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("products");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-lg bg-slate-800 text-white font-semibold hover:bg-slate-900 transition-all duration-300 hover:scale-105 shadow-lg shadow-slate-800/20"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </button>
            </div>
          </section>
          
          {/* Projects Preview - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="projects_badge" 
                  value="Our Work"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="projects"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="projects_title" 
                  value="Featured Projects"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="projects"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableManufacturingProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="group cursor-pointer rounded-lg overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-200 hover:border-amber-300 animate-card-fly"
                  onClick={() => openProjectModal(project)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={project.imageField} className="block h-full w-full overflow-hidden">
                      <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-slate-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={project.titleKey} 
                        value={project.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="projects"
                      />
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={project.descKey} 
                        value={project.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="projects"
                      />
                    </p>
                    <div className="mt-3 text-amber-600 text-xs font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="view_project_button" 
                        value="View Project"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="projects"
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Journal Preview - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8 bg-slate-50">
            <div className="text-center mb-8">
              <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="journal_badge" 
                  value="Manufacturing Insights"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="journal_title" 
                  value="Latest Articles"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableManufacturingPosts.slice(0, 3).map((post) => (
                <div 
                  key={post.id} 
                  className="group cursor-pointer rounded-lg overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-200 hover:border-amber-300"
                  onClick={() => openBlogModal(post)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-amber-600 font-semibold">{post.category}</span>
                      <span className="text-xs text-slate-400">•</span>
                      <span className="text-xs text-slate-400">{post.date}</span>
                    </div>
                    <h4 className="font-semibold text-slate-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.titleKey} 
                        value={post.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.descKey} 
                        value={post.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </p>
                    <div className="mt-3 text-amber-600 text-xs font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== PRODUCTS PAGE ==================== */}
      {activePage === "products" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_page_badge" 
                value="Our Catalog"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_page_title" 
                value="Industrial Products"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_page_subtitle" 
                value="Premium quality industrial solutions for your business"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Products Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {manufacturingCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-slate-800 text-white shadow-lg" 
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {getFilteredItems(activeCategory).map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-slate-200 hover:border-amber-300 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-slate-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-lg text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-slate-800/80 text-slate-100 text-[10px] font-bold px-3 py-1 rounded-lg">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-slate-500">{product.brand}</span>
                    <span className="text-xs text-slate-300">•</span>
                    <span className="text-xs text-slate-500">{product.category}</span>
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="products"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-amber-600">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="products"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-slate-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {product.type} • {product.power || product.material || product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PROJECTS PAGE ==================== */}
      {activePage === "projects" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
              <InlineEditableTextWithNav 
                p={p} 
                field="projects_page_badge" 
                value="Our Portfolio"
                onNavigate={handleNavigateAfterEdit}
                navPage="projects"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="projects_page_title" 
                value="Featured Projects"
                onNavigate={handleNavigateAfterEdit}
                navPage="projects"
              />
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="projects_page_subtitle" 
                value="Delivering excellence across industries"
                onNavigate={handleNavigateAfterEdit}
                navPage="projects"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Industry Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {industriesServed.map((industry) => (
              <button
                key={industry.key}
                onClick={() => setActiveIndustry(industry.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeIndustry === industry.key 
                    ? "bg-slate-800 text-white shadow-lg" 
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {industry.icon} {industry.label}
              </button>
            ))}
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-slate-200 hover:border-amber-300"
                onClick={() => openProjectModal(project)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={project.imageField} className="block h-full w-full overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={project.titleKey} 
                      value={project.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="projects"
                    />
                  </h3>
                  <p className="text-slate-500 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={project.descKey} 
                      value={project.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="projects"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="text-slate-500">Client: {project.client}</span>
                    <span className="text-amber-600 font-semibold">{project.value}</span>
                  </div>
                  <div className="mt-3 text-amber-600 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="view_project_button" 
                      value="View Project Details"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="projects"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== CAPABILITIES PAGE ==================== */}
      {activePage === "capabilities" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
              <InlineEditableTextWithNav 
                p={p} 
                field="capabilities_page_badge" 
                value="Our Expertise"
                onNavigate={handleNavigateAfterEdit}
                navPage="capabilities"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="capabilities_page_title" 
                value="Manufacturing Capabilities"
                onNavigate={handleNavigateAfterEdit}
                navPage="capabilities"
              />
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="capabilities_page_subtitle" 
                value="State-of-the-art manufacturing solutions"
                onNavigate={handleNavigateAfterEdit}
                navPage="capabilities"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {manufacturingCapabilities.map((cap) => (
              <div 
                key={cap.id} 
                className="group bg-white rounded-lg p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-200 hover:border-amber-300"
              >
                <div className="flex items-start gap-4">
                  <div className="text-5xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    {cap.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cap_${cap.id}_title`} 
                        value={cap.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="capabilities"
                      />
                    </h3>
                    <p className="text-slate-500 mt-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cap_${cap.id}_desc`} 
                        value={cap.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="capabilities"
                      />
                    </p>
                    <div className="mt-4 h-0.5 w-16 bg-amber-500 group-hover:w-32 transition-all duration-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-sm tracking-[0.2em] uppercase font-semibold">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_title" 
                value="Manufacturing Insights"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_subtitle" 
                value="Latest trends and insights in manufacturing"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-300 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableManufacturingPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-slate-200 hover:border-amber-300"
                onClick={() => openBlogModal(post)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-lg font-semibold">
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400">{post.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">By {post.author}</span>
                    <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <span>→</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-600 text-sm tracking-wider uppercase font-semibold">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Apex Manufacturing"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Engineering Excellence Since 1995"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-amber-500 mt-4 mb-6" />
              <p className="text-slate-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Apex Manufacturing is a leading industrial solutions provider with over 30 years of experience. We specialize in precision engineering, custom fabrication, and automated manufacturing."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our commitment to quality, innovation, and customer satisfaction has made us a trusted partner for industries worldwide. We combine advanced technology with expert craftsmanship."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-amber-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-slate-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Projects Completed"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="150+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-slate-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Expert Team"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-slate-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Global Clients"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-2xl border-4 border-slate-200/50">
              <InlineEditableImage p={p} field="apexAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.apexAboutImage || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80"} 
                  alt="About Apex Manufacturing" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🏭 Shop"],
            ["services", "⚙️ Services"],
            ["details", "📖 About"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-slate-800 text-white shadow-lg" : "text-slate-500 hover:text-slate-800"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
   
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal - FIXED with InlineEditableImage */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-lg overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-lg text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-slate-800/80 text-slate-100 text-xs font-bold px-3 py-1 rounded-lg">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-500">{selectedProduct.brand}</span>
                <span className="text-xs text-slate-300">•</span>
                <span className="text-xs text-slate-500">{selectedProduct.category}</span>
                <span className="text-xs text-slate-300">•</span>
                <span className="text-xs text-slate-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-800">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-slate-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.power && (
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">
                    ⚡ {selectedProduct.power}
                  </span>
                )}
                {selectedProduct.warranty && (
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-lg">
                    🛡️ {selectedProduct.warranty}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-amber-600">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-slate-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Project Modal - FIXED with InlineEditableImage */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-lg overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-800">Project Details</h3>
              <button 
                onClick={closeProjectModal} 
                className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedProject.imageField} className="block w-full overflow-hidden rounded-lg">
                  <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProject.titleKey} 
                  value={selectedProject.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="projects"
                  isInModal={true}
                />
              </h2>
              <p className="text-slate-600 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProject.descKey} 
                  value={selectedProject.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="projects"
                  isInModal={true}
                />
              </p>
              <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-semibold text-slate-800">{selectedProject.client}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Duration</p>
                  <p className="font-semibold text-slate-800">{selectedProject.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Project Value</p>
                  <p className="font-semibold text-amber-600">{selectedProject.value}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <p className="font-semibold text-green-600">Completed</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeProjectModal} 
                  className="px-6 py-2.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="projects"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-lg overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-800">Manufacturing Journal</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-lg">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-lg font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 border-b border-slate-100 pb-4 mb-4">
                <span>{selectedPost.date}</span>
                <span>•</span>
                <span>By {selectedPost.author}</span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes industrial-particle { 
          0% { transform: translate(0, 0) scale(1); opacity: 0.03; }
          50% { transform: translate(20px, -30px) scale(1.5); opacity: 0.08; }
          100% { transform: translate(0, -60px) scale(1); opacity: 0.03; }
        }
        .industrial-particle { animation: industrial-particle 7s ease-in-out infinite; border-radius: 2px; }
        
        @keyframes geometric-square {
          0% { transform: rotate(0deg) scale(1); opacity: 0.05; }
          50% { transform: rotate(180deg) scale(1.2); opacity: 0.12; }
          100% { transform: rotate(360deg) scale(1); opacity: 0.05; }
        }
        .geometric-square { animation: geometric-square 8s ease-in-out infinite; border: 1px solid; }
        
        @keyframes geometric-circle {
          0% { transform: scale(1); opacity: 0.05; }
          50% { transform: scale(1.3); opacity: 0.1; }
          100% { transform: scale(1); opacity: 0.05; }
        }
        .geometric-circle { animation: geometric-circle 6s ease-in-out infinite; border: 1px solid; border-radius: 50%; }
        
        @keyframes geometric-triangle {
          0% { transform: rotate(0deg) scale(1); opacity: 0.05; }
          50% { transform: rotate(120deg) scale(1.2); opacity: 0.1; }
          100% { transform: rotate(240deg) scale(1); opacity: 0.05; }
        }
        .geometric-triangle { animation: geometric-triangle 10s ease-in-out infinite; border: 1px solid; clip-path: polygon(50% 0%, 0% 100%, 100% 100%); }
        
        @keyframes geometric-diamond {
          0% { transform: rotate(0deg) scale(1); opacity: 0.05; }
          50% { transform: rotate(90deg) scale(1.2); opacity: 0.1; }
          100% { transform: rotate(180deg) scale(1); opacity: 0.05; }
        }
        .geometric-diamond { animation: geometric-diamond 9s ease-in-out infinite; border: 1px solid; transform: rotate(45deg); }
        
        @keyframes geometric-hexagon {
          0% { transform: rotate(0deg) scale(1); opacity: 0.05; }
          50% { transform: rotate(60deg) scale(1.2); opacity: 0.1; }
          100% { transform: rotate(120deg) scale(1); opacity: 0.05; }
        }
        .geometric-hexagon { animation: geometric-hexagon 7s ease-in-out infinite; border: 1px solid; clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%); }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9) rotate(-2deg); } 60% { transform: translateY(-5px) scale(1.02) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}